import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type ContactFormSchema, contactSchema } from "@msuite/utils/schemas";
import type { Tables } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { ContactForm } from "@/forms/contact-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface ProjectOverrideContext {
	projectId: string;
}

interface EditContactModalProps extends ModalProps {
	contactId: string;
	onComplete?: (contact: Tables<"contacts">) => void;
	projectOverride?: ProjectOverrideContext;
}

export function EditContactModal({
	isOpen,
	onClose,
	contactId,
	onComplete,
	projectOverride,
}: EditContactModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const baseResponse = await client.contacts[":contactId"].$get({ param: { contactId } });
		const baseData = await responseHandler(baseResponse);
		if (!projectOverride) return baseData.contact as ContactFormSchema;

		const overrideResponse = await client.projects[":projectId"].contacts[
			":contactId"
		].override.$get({
			param: { projectId: projectOverride.projectId, contactId },
		});
		const overrideData = await responseHandler(overrideResponse, false);
		return {
			...(baseData.contact as ContactFormSchema),
			...((overrideData?.override?.overrides as object) ?? {}),
		} as ContactFormSchema;
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(contactSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: ContactFormSchema) {
		try {
			if (projectOverride) {
				const response = await client.projects[":projectId"].contacts[":contactId"].override.$patch(
					{
						param: { projectId: projectOverride.projectId, contactId },
						json: json as unknown as Record<string, unknown>,
					},
				);
				const data = await responseHandler(response);
				if (data.success) {
					onClose();
					onComplete?.(json as unknown as Tables<"contacts">);
				}
				return;
			}
			const response = await client.contacts[":contactId"].$patch({ param: { contactId }, json });
			const data = await responseHandler(response);
			if (data.contact) {
				onClose();
				onComplete?.(data.contact);
			}
		} catch (error) {
			console.error(error);
			toast.error(t("general.something_went_wrong"));
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={
					projectOverride ? t("contacts.edit_contact_project_override") : t("contacts.edit_contact")
				}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isOpen={isOpen}
				onClose={onClose}
				isLoading={methods.formState.isLoading && !loadFailed}
				data-cy="contact-member-form"
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ContactForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
